import { useBreakpointDown } from '@all-manager/design-system-v2';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpacePath } from '../../../../hooks/useSpacePath';
import useGetNotifications from '../../api/useGetNotifications';
import useGetUnreadCount from '../../api/useGetUnreadCount';
import useMarkNotificationRead from '../../api/useMarkNotificationRead';
import useMarkAllNotificationsRead from '../../api/useMarkAllNotificationsRead';
import { EnrichedNotification } from '../../types/notification';
import { buildNotificationMessage } from '../../utils/buildNotificationMessage';
import { flattenUniquePages, getId } from '../../../../utils/pagination';

export function useNotificationsBell() {
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isPhone = useBreakpointDown('sm');

  const isOpen = Boolean(anchorEl);

  // Only fetch (and enrich) the list once the popover is open. The badge relies
  // on the lightweight count query, which stays always-on.
  const {
    data: notificationPages,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useGetNotifications({ enabled: isOpen });
  const { data: unreadCount } = useGetUnreadCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  function handleOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleNotificationClick(notification: EnrichedNotification) {
    if (!notification.read_at) {
      markRead.mutate(notification.id);
    }

    if (notification.type === 'event_created' && notification.event) {
      handleClose();
      navigate(spacePath(`/topics/${notification.event.subject_id}`));
      return;
    }

    if (
      (notification.type === 'reminder_due' || notification.type === 'reminder_due_soon') &&
      notification.reminder
    ) {
      const { subject_id, provider_id } = notification.reminder;

      if (subject_id) {
        handleClose();
        navigate(spacePath(`/topics/${subject_id}`));
      } else if (provider_id) {
        handleClose();
        navigate(spacePath(`/providers/${provider_id}`));
      }
    }
  }

  function handleLoadMore() {
    fetchNextPage();
  }

  function handleMarkAllRead() {
    markAllRead.mutate();
  }

  const count = unreadCount ?? 0;
  const items = flattenUniquePages(notificationPages?.pages, getId).map((notification) => ({
    notification,
    message: buildNotificationMessage(notification),
    relativeDate: formatDistanceToNow(parseISO(notification.created_at), { addSuffix: true, locale: fr }),
  }));

  return {
    anchorEl,
    isOpen,
    isPhone,
    isLoading,
    items,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    isLoadMoreError: isFetchNextPageError,
    unreadCount: count,
    buttonLabel: count > 0 ? `Notifications, ${count} non lue${count > 1 ? 's' : ''}` : 'Notifications',
    handleOpen,
    handleClose,
    handleNotificationClick,
    handleMarkAllRead,
    handleLoadMore,
  };
}
